import React, { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { Box, Divider } from "@mui/material";
import { CustomTypography } from "shared/ui/CustomTypography";
import { GlassEmptyState } from "shared/ui/GlassEmptyState";
import { LIMEState } from "features/lime/state/limeAtoms";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const subtitleSx = {
  display: "flex",
  alignItems: "center",
};

const transformData = (limeData: { [parameter: string]: number }) => {
  return Object.entries(limeData).map(([key, value]) => ({
    name: key,
    value: value,
  }));
};

export const LIMEResultViewer: React.FC = () => {
  const lime = useRecoilValue(LIMEState);
  const limeData = lime ?? {};
  const hasLimeData = Object.keys(limeData).length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(300);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateSizes = () => {
      if (containerRef.current) {
        setHeight(containerRef.current.clientHeight);
      }
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.clientHeight);
      }
    };

    const resizeObserver = new ResizeObserver(updateSizes);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      component="div"
      sx={{
        boxSizing: "border-box",
        flexGrow: hasLimeData ? 1 : 0,
        paddingTop: 1,
        height: hasLimeData ? "100%" : "auto",
      }}
    >
      {/* ヘッダー部分の高さを測定 */}
      <Box ref={headerRef} component="div">
        <Box component="div" sx={subtitleSx}>
          <CustomTypography>LIME Results</CustomTypography>
        </Box>
        <Divider sx={{ my: 0.5, bgcolor: "#e1e1e1" }} />
      </Box>

      {hasLimeData ? (
        <ResponsiveContainer width="100%" height={height - headerHeight - 130}>
          <BarChart layout="vertical" data={transformData(limeData)}>
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: "#e1e1e1" }}
            />
            <YAxis
              dataKey="name"
              type="category"
              interval={0}
              tick={{ fontSize: 10, fill: "#e1e1e1" }}
              width={120}
            />
            <Tooltip />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box component="div" sx={{ paddingBottom: 0.5 }}>
          <GlassEmptyState message="No data to display graphically." />
        </Box>
      )}
    </Box>
  );
};
